// src/api/apiEndpoints.js
import { API_BASE_URL } from '../configs/configs.js';

export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: `${API_BASE_URL}/Auth/login`,
    REGISTER: `${API_BASE_URL}/Users`, // User registration endpoint
    PROFILE: `${API_BASE_URL}/Auth/profile`,
    FORGOT_PASSWORD: `${API_BASE_URL}/Auth/forgot-password`,
    RESET_PASSWORD: `${API_BASE_URL}/Auth/reset-password`,
    VERIFY_EMAIL: `${API_BASE_URL}/Auth/verify-email`,
  },

  // Users
  USERS: {
    REGISTER: `${API_BASE_URL}/Users`,
    GET_ALL: `${API_BASE_URL}/Users`,
    GET_BY_ID: id => `${API_BASE_URL}/Users/${id}`,
    ME: `${API_BASE_URL}/Users/me`,
    CHANGE_PASSWORD: `${API_BASE_URL}/Users/me/change-password`,
    SEND_VERIFICATION: `${API_BASE_URL}/Users/me/send-verification`,
    GET_BY_EMAIL: email => `${API_BASE_URL}/Users/by-email/${encodeURIComponent(email)}`,
    GET_BY_PHONE: phone => `${API_BASE_URL}/Users/by-phone/${phone}`,
    UPDATE: id => `${API_BASE_URL}/Users/${id}`, // admin: PUT /Users/{id}
    DELETE: id => `${API_BASE_URL}/Users/${id}`, // admin: soft delete (status -> Inactive)
  },

  // Products
  PRODUCTS: {
    CREATE: `${API_BASE_URL}/Products`,
    GET_ALL: `${API_BASE_URL}/Products`,
    GET_BY_ID: id => `${API_BASE_URL}/Products/${id}`,
    UPDATE: id => `${API_BASE_URL}/Products/${id}`,
    DELETE: id => `${API_BASE_URL}/Products/${id}`,
    GET_TRENDING: `${API_BASE_URL}/Products/trending`,
  },

  // Categories
  CATEGORY: {
    CREATE: `${API_BASE_URL}/Category`,
    GET_ALL: `${API_BASE_URL}/Category`,
    GET_BY_ID: id => `${API_BASE_URL}/Category/${id}`,
    UPDATE: id => `${API_BASE_URL}/Category/${id}`,
    DELETE: id => `${API_BASE_URL}/Category/${id}`,
    GET_BY_NAME: name => `${API_BASE_URL}/Category/by-name/${name}`,
  },

  // Orders
  ORDERS: {
    CREATE: `${API_BASE_URL}/Orders`,
    CREATE_DIGITAL: `${API_BASE_URL}/Orders/digital`,
    GET_ALL: `${API_BASE_URL}/Orders`,
    GET_BY_ID: id => `${API_BASE_URL}/Orders/${id}`,
    GET_BY_ORDER_ID: orderId => `${API_BASE_URL}/Orders/order/${orderId}`,
    GET_MY_ORDERS: `${API_BASE_URL}/Orders/my-orders`,
    GET_PAYMENT_STATUS: orderId =>
      `${API_BASE_URL}/Orders/payment-status/${orderId}`,
    UPDATE: orderId => `${API_BASE_URL}/Orders/${orderId}`,
    UPDATE_STATUS: `${API_BASE_URL}/Orders/update-status`,
    GRANT_ACCESS: orderId => `${API_BASE_URL}/Orders/${orderId}/grant-access`,
  },

  // Reviews
  REVIEWS: {
    CREATE: `${API_BASE_URL}/Review`,
    GET_ALL: `${API_BASE_URL}/Review`,
    GET_BY_PRODUCT: productId => `${API_BASE_URL}/Review/product/${productId}`,
    GET_BY_USER: userId => `${API_BASE_URL}/Review/user/${userId}`,
    UPDATE: reviewId => `${API_BASE_URL}/Review/${reviewId}`,
    DELETE: reviewId => `${API_BASE_URL}/Review/${reviewId}`,
    GET_AVERAGE_RATING: productId =>
      `${API_BASE_URL}/Review/product/${productId}/average-rating`,
    CAN_REVIEW: productId => `${API_BASE_URL}/Review/can-review/${productId}`,
    CHECK_USER_REVIEW: (userId, productId) =>
      `${API_BASE_URL}/Review/check/${userId}/${productId}`,
  },

  // GitHub Codes
  GITHUB: {
    CREATE: `${API_BASE_URL}/Github`,
    GET_ALL: `${API_BASE_URL}/Github`,
    GET_CODES: `${API_BASE_URL}/Github`,
    GET_BY_ID: id => `${API_BASE_URL}/Github/${id}`,
    UPDATE: id => `${API_BASE_URL}/Github/${id}`,
    DELETE: id => `${API_BASE_URL}/Github/${id}`,
    SEARCH_USER: username => `${API_BASE_URL}/Github/search-user?username=${username}`,
    VERIFY_ORDER_ACCESS: orderId => `${API_BASE_URL}/Github/verify-order-access/${orderId}`,
  },

  // Deliverables
  DELIVERABLES: {
    CREATE: `${API_BASE_URL}/api/Deliverable`,
    GET_BY_ORDER: orderId => `${API_BASE_URL}/api/Deliverable/order/${orderId}`,
    DELETE_BY_PRODUCT: productId =>
      `${API_BASE_URL}/api/Deliverable/product/${productId}`,
    TRACK_ACCESS: `${API_BASE_URL}/api/Deliverable/track-access`,
    MANUAL_ATTACH: `${API_BASE_URL}/api/Deliverable/manual-attach`,
    OPEN: orderId => `${API_BASE_URL}/api/Deliverable/order/${orderId}/open`,
  },

  // Checkout (server-side pricing, order creation, payment retries)
  CHECKOUT: {
    SETTINGS: `${API_BASE_URL}/api/checkout/settings`,
    QUOTE: productId => `${API_BASE_URL}/api/checkout/quote/${productId}`,
    CREATE: `${API_BASE_URL}/api/checkout`,
    PAY: orderId => `${API_BASE_URL}/api/checkout/${orderId}/pay`,
  },

  // Admin: delivery monitoring
  FULFILLMENT: {
    ATTENTION: `${API_BASE_URL}/api/fulfillment/attention`,
    ORDER: orderId => `${API_BASE_URL}/api/fulfillment/order/${orderId}`,
    RETRY: orderId => `${API_BASE_URL}/api/fulfillment/order/${orderId}/retry`,
    ACCESS: `${API_BASE_URL}/api/fulfillment/access`,
    ACCESS_REVOKE: orderId => `${API_BASE_URL}/api/fulfillment/order/${orderId}/access/revoke`,
    ACCESS_EXTEND: orderId => `${API_BASE_URL}/api/fulfillment/order/${orderId}/access/extend`,
  },

  // GitHub repository invitations of an order (customer: own orders; admin: any)
  GITHUB_INVITES: {
    ORDER: orderId => `${API_BASE_URL}/api/github-invites/order/${encodeURIComponent(orderId)}`,
    RESEND: orderId => `${API_BASE_URL}/api/github-invites/order/${encodeURIComponent(orderId)}/resend`,
  },

  // GST invoices
  INVOICES: {
    BY_ORDER: orderId => `${API_BASE_URL}/api/invoices/order/${orderId}`,
    HTML: orderId => `${API_BASE_URL}/api/invoices/order/${orderId}/html`,
    ISSUE: orderId => `${API_BASE_URL}/api/invoices/order/${orderId}/issue`,
    EXPORT: `${API_BASE_URL}/api/invoices/export`,
    STATUS: `${API_BASE_URL}/api/invoices/status`,
  },

  // Provider-agnostic payments
  PAYMENTS: {
    METHODS: `${API_BASE_URL}/api/payments/methods`,
    SUBMIT_PROOF: paymentId => `${API_BASE_URL}/api/payments/${paymentId}/proof`,
    REVIEW: paymentId => `${API_BASE_URL}/api/payments/${paymentId}/review`,
    SYNC: paymentId => `${API_BASE_URL}/api/payments/${paymentId}/sync`,
  },

  // Payment (legacy routes)
  PAYMENT: {
    UPDATE_STATUS: `${API_BASE_URL}/api/Payment/update-payment-status`,
    UPDATE_MANUAL_STATUS: `${API_BASE_URL}/api/Payment/update-payment-status`,
    PHONEPE_WEBHOOK: `${API_BASE_URL}/api/Payment/phonepe-webhook`,
    GET_TRANSACTIONS: `${API_BASE_URL}/api/Payment/transactions`,
    GET_BY_ORDER: orderId => `${API_BASE_URL}/api/Payment/order/${orderId}`,
    GET_BY_ID: id => `${API_BASE_URL}/api/Payment/${id}`,
  },

  // Dashboard
  DASHBOARD: {
    GET_STATS: `${API_BASE_URL}/api/Dashboard`,
  },

  // Videos: see src/services/videoService.js (/ProjectVideo)

  // Health
  HEALTH: {
    CHECK: `${API_BASE_URL}/Health`,
    DATABASE: `${API_BASE_URL}/Health/database`,
  },
};
